import React, { useMemo } from 'react';
import { Provider, useStore } from 'react-redux';
import { FACEDOWN, PLAYER_STATUS } from 'constants/gameConstants';
import { GameProvider } from 'contexts/GameContext';
import { HandHistorySnapshotModeContext } from 'contexts/HandHistorySnapshotModeContext';
import { OrientationOverrideProvider } from 'contexts/OrientationOverrideContext';
import { ShowAllDealersContext } from 'contexts/ShowAllDealersContext';
import { TableIdContext } from 'contexts/TableIdContext';
import {
  buildTableDemo,
  Currency,
  GameVariant,
  NINE_SEAT_DEMO_POV_GUID,
  PotScenario,
  SeatCardMode
} from 'demo/nineSeatTableDemo';
import { useOrientation } from 'hooks/useOrientation';
import {
  currentTableSlice,
  updateCurrentPlayerGUID,
  updateCurrentTable
} from 'reducers/currentTable';
import { patchPlayerProfile } from 'reducers/user';
import { createAppStore, RootState } from 'store';

import Table from 'components/Table';

import { formatPlayerDisplayName } from './format';
import { snapshotHandRank } from './snapshotHandRank';
import {
  BettingStructure,
  CardRef,
  GameType,
  GameVariantId,
  HandMetadata,
  PotGroupPlayerSnapshot,
  PotGroupSnapshot,
  TableSnapshot as TableSnapshotType
} from './types';

import styles from './HandHistory.module.scss';

type SnapshotWinner = {
  seat: number;
  win: number;
  cards: string;
  hand: string;
  handType: string;
};

const seatModeForSnapshot = (snapshot: TableSnapshotType) =>
  [...snapshot.seats]
    .sort((a, b) => a.seat - b.seat)
    .reduce<Record<number, SeatCardMode>>((modes, seat, index) => {
      const demoSeat = index + 1;

      if (seat.folded) {
        modes[demoSeat] = 'fold';
        return modes;
      }

      modes[demoSeat] = seat.cardsRevealed ? 'show' : 'down';
      return modes;
    }, {});

const withoutBets = (
  payload: ReturnType<typeof buildTableDemo>,
  dealerSeat: number | undefined
) => {
  const next: ReturnType<typeof buildTableDemo> = {
    ...payload,
    game: {
      ...payload.game,
      dealer: dealerSeat ? String(dealerSeat) : '',
      dealerVisible: Boolean(dealerSeat),
      move: '',
      sbPlayer: '',
      bbPlayer: ''
    },
    players: payload.players.map((player: any) => ({
      ...player,
      bet: '',
      lbet: 0,
      betDisplay: '',
      action: ''
    }))
  };

  for (let seat = 1; seat <= 10; seat++) {
    next.game[`p${seat}bet`] = '';
    next.game[`p${seat}lbet`] = 0;
    next.game[`p${seat}action`] = '';
  }

  return next;
};

const withSnapshotWinners = (
  payload: ReturnType<typeof buildTableDemo>,
  winners: SnapshotWinner[],
  potTotal: number
) => {
  return {
    ...payload,
    game: {
      ...payload.game,
      handHistorySnapshot: true,
      // The demo scaffold supplies a hard-coded live hand-rank label. Snapshot
      // results come from the recorded winners, never from that placeholder.
      handRank: '',
      pot: potTotal,
      totalPot: potTotal,
      showdown: {
        ...payload.game.showdown,
        currentPot: 0,
        currentStep: 3
      },
      pots: payload.game.pots.map((pot: any, index: number) =>
        index === 0
          ? {
              ...pot,
              pot: potTotal,
              winners: winners.map((winner) => ({
                player: String(winner.seat),
                cards: winner.cards,
                hand: winner.hand,
                win: winner.win,
                handType: winner.handType
              }))
            }
          : pot
      )
    },
    gameState: {
      ...payload.gameState,
      isShowdown: true
    }
  };
};

const normalizeCard = (card: string | undefined) => {
  if (!card || card === FACEDOWN) return '';
  return card.toUpperCase();
};

const getBoardStage = (boardLength: number) => {
  if (boardLength >= 5) return 'river';
  if (boardLength === 4) return 'turn';
  if (boardLength >= 3) return 'flop';
  return 'preflop';
};

const winnerHandType = (potName: string) => {
  if (/\blo\b/i.test(potName)) return 'LO';
  if (/\bhi\b/i.test(potName)) return 'HI';
  return '';
};

const winnerCards = (player: PotGroupPlayerSnapshot, board: CardRef[]) => {
  if (!player.cardsRevealed || player.folded || player.mucked) return '';
  const cards = player.winningCards?.length
    ? player.winningCards
    : [...(player.cards ?? []), ...board];

  return cards.map(normalizeCard).filter(Boolean).join(',');
};

const buildGrossWinBySeat = (potGroups: PotGroupSnapshot[], seatToDemoSeat: Map<number, number>) =>
  potGroups.reduce<Record<number, number>>((totals, pot) => {
    const winners = pot.players.filter((player) => player.isWinner || player.net > 0);
    const positiveTotal = winners.reduce((sum, player) => sum + Math.max(player.net, 0), 0);

    winners.forEach((player) => {
      const demoSeat = seatToDemoSeat.get(player.seat);
      if (!demoSeat) return;

      const share =
        typeof player.grossWin === 'number' && player.grossWin >= 0
          ? player.grossWin
          : positiveTotal > 0
          ? (Math.max(player.net, 0) / positiveTotal) * pot.amount
          : pot.amount / Math.max(winners.length, 1);
      totals[demoSeat] = (totals[demoSeat] ?? 0) + share;
    });

    return totals;
  }, {});

const withSnapshotSeats = (
  payload: ReturnType<typeof buildTableDemo>,
  seats: TableSnapshotType['seats'],
  board: CardRef[],
  grossWinBySeat: Record<number, number>,
  viewerSeat?: number
) => {
  const sortedSeats = [...seats].sort((a, b) => a.seat - b.seat);
  const visibleSeats: number[] = [];
  const showCardSeats: number[] = [];
  const nextGame = { ...payload.game };
  const boardCards = board.map(normalizeCard);

  for (let cardIndex = 0; cardIndex < 5; cardIndex++) {
    nextGame[`card${cardIndex + 1}`] = boardCards[cardIndex] ?? '';
  }

  const nextPlayers = payload.players.map((player: any, index: number) => {
    if (index === 0) return player;

    const seat = sortedSeats[index - 1];
    if (!seat) return player;

    const isViewer = seat.seat === viewerSeat;
    const folded = Boolean(seat.folded);
    const mucked = Boolean(seat.mucked);
    const cards = folded ? [] : seat.cards?.map(normalizeCard).filter(Boolean) ?? [];
    const hasCards = cards.length > 0;
    const action = folded ? 'fold' : '';

    nextGame[`p${index}bet`] = folded ? 'F' : '';
    nextGame[`p${index}lbet`] = 0;
    nextGame[`p${index}action`] = action;
    nextGame[`p${index}status`] = mucked ? PLAYER_STATUS.MUCK_CARDS : PLAYER_STATUS.ACTIVE;

    for (let cardIndex = 0; cardIndex < 4; cardIndex++) {
      nextGame[`p${index}card${cardIndex + 1}`] = cards[cardIndex] ?? '';
    }

    if (hasCards) {
      visibleSeats.push(index);
    }

    if (hasCards && seat.cardsRevealed) {
      showCardSeats.push(index);
    }

    return {
      ...player,
      GUID: isViewer ? NINE_SEAT_DEMO_POV_GUID : `hand-history-seat-${seat.seat}`,
      ID: index,
      stack: Math.max((seat.stack ?? player.stack) - (grossWinBySeat[index] ?? 0), 0),
      bet: folded ? 'F' : '',
      lbet: 0,
      betDisplay: '',
      action,
      status: mucked ? PLAYER_STATUS.MUCK_CARDS : PLAYER_STATUS.ACTIVE,
      displayName: formatPlayerDisplayName(seat.name, isViewer),
      avatar: seat.avatar ?? player.avatar
    };
  });

  return {
    ...payload,
    game: {
      ...nextGame,
      seatsWithVisibleCards: visibleSeats,
      seatsWithShowCard: showCardSeats
    },
    players: nextPlayers
  };
};

const withSnapshotMetadata = (
  payload: ReturnType<typeof buildTableDemo>,
  metadata: HandMetadata,
  bettingStructure: BettingStructure,
  gameVariant: GameVariantId,
  gameType: GameType
) => {
  const smallBlind = metadata.smallBlind ?? payload.game.sb;
  const bigBlind = metadata.bigBlind ?? payload.game.bb;
  const variantLabel =
    gameVariant === 'omaha' ? (bettingStructure.includes('HiLo') ? 'PLO8' : 'PLO') : 'NLH';

  return {
    ...payload,
    game: {
      ...payload.game,
      game: gameVariant === 'omaha' ? 'omaha' : 'holdem',
      gameType: bettingStructure,
      tabletype: gameType === 'cash' ? 's' : 't',
      sb: smallBlind,
      bb: bigBlind,
      ante: metadata.ante ?? 0,
      lvlDisplay: metadata.level ?? 1,
      currentSB: smallBlind,
      currentBB: bigBlind,
      currentAnte: metadata.ante ?? 0,
      nextLevel: -1,
      blindLevels:
        gameType === 'cash'
          ? `$${smallBlind}/$${bigBlind} ${variantLabel}`
          : `Level ${metadata.level ?? 1} · ${smallBlind}/${bigBlind}${
              metadata.ante ? ` (${metadata.ante})` : ''
            } ${variantLabel}`
    }
  };
};

// Preview-only adapter: seed the unchanged game table with a completed sample hand.
export const HandHistoryDemoTable = ({
  snapshot,
  gameType,
  gameVariant,
  bettingStructure,
  metadata,
  potTotal,
  potGroups
}: {
  snapshot: TableSnapshotType;
  gameType: GameType;
  gameVariant: GameVariantId;
  bettingStructure: BettingStructure;
  metadata: HandMetadata;
  potTotal: number;
  potGroups: PotGroupSnapshot[];
}) => {
  const parentStore = useStore<RootState>();
  const viewportOrientation = useOrientation();
  const sortedSeats = useMemo(
    () => [...snapshot.seats].sort((a, b) => a.seat - b.seat),
    [snapshot.seats]
  );
  const playerCount = Math.max(2, sortedSeats.length);
  const viewerSeat = Math.max(
    1,
    sortedSeats.findIndex((seat) => seat.seat === snapshot.viewerSeat) + 1 || 1
  );
  const variant: GameVariant = gameVariant;
  // This settled table shows the consolidated pot; the breakdown below carries
  // individual pots. No Payout and Hi/Lo groups must not create demo side pots.
  const potScenario: PotScenario = 'main';
  const currency: Currency = gameType === 'cash' ? 'cents' : 'dollars';
  const seatModes = useMemo(() => seatModeForSnapshot(snapshot), [snapshot]);
  const seatToDemoSeat = useMemo(
    () => new Map(sortedSeats.map((seat, index) => [seat.seat, index + 1])),
    [sortedSeats]
  );
  const dealerSeat = useMemo(() => {
    const sourceDealerSeat = snapshot.dealerSeat ?? sortedSeats.find((seat) => seat.isDealer)?.seat;

    return sourceDealerSeat ? seatToDemoSeat.get(sourceDealerSeat) : undefined;
  }, [seatToDemoSeat, snapshot.dealerSeat, sortedSeats]);
  const grossWinBySeat = useMemo(
    () => buildGrossWinBySeat(potGroups, seatToDemoSeat),
    [potGroups, seatToDemoSeat]
  );
  const snapshotWinners = useMemo(() => {
    const winnersBySeat = new Map<number, SnapshotWinner>();

    potGroups.forEach((pot) => {
      pot.players
        .filter((player) => player.isWinner || player.net > 0)
        .forEach((player) => {
          const demoSeat = seatToDemoSeat.get(player.seat);
          if (!demoSeat || winnersBySeat.has(demoSeat)) return;

          winnersBySeat.set(demoSeat, {
            seat: demoSeat,
            win: grossWinBySeat[demoSeat] ?? Math.max(player.net, 0),
            cards: winnerCards(player, snapshot.board),
            hand: player.handRank ?? player.loLabel ?? '',
            handType: player.handType ?? winnerHandType(pot.name)
          });
        });
    });

    return Array.from(winnersBySeat.values());
  }, [grossWinBySeat, potGroups, seatToDemoSeat, snapshot.board, snapshot.viewerSeat]);

  const snapshotStore = useMemo(() => {
    const store = createAppStore({
      ...parentStore.getState(),
      currentTable: currentTableSlice.getInitialState(),
      tables: {}
    });
    const payload = withSnapshotWinners(
      withSnapshotSeats(
        withoutBets(
          withSnapshotMetadata(
            buildTableDemo(
              playerCount,
              potScenario,
              2,
              currency,
              0,
              viewerSeat,
              true,
              seatModes,
              getBoardStage(snapshot.board.length),
              false,
              {},
              variant,
              0
            ),
            metadata,
            bettingStructure,
            gameVariant,
            gameType
          ),
          dealerSeat
        ),
        sortedSeats,
        snapshot.board,
        grossWinBySeat,
        snapshot.viewerSeat
      ),
      snapshotWinners,
      potTotal
    );

    const viewer = sortedSeats.find((seat) => seat.seat === snapshot.viewerSeat);
    payload.game.handRank =
      viewer && !viewer.folded && !viewer.mucked
        ? snapshotHandRank(viewer.cards || [], snapshot.board, gameVariant)
        : '';
    store.dispatch(patchPlayerProfile({ playerGUID: NINE_SEAT_DEMO_POV_GUID }));
    store.dispatch(updateCurrentPlayerGUID(NINE_SEAT_DEMO_POV_GUID));
    store.dispatch(updateCurrentTable(payload));
    return store;
  }, [
    bettingStructure,
    currency,
    dealerSeat,
    gameVariant,
    gameType,
    grossWinBySeat,
    metadata,
    parentStore,
    potTotal,
    potScenario,
    playerCount,
    seatModes,
    seatToDemoSeat,
    snapshot.board,
    snapshot.board.length,
    snapshot.viewerSeat,
    snapshotWinners,
    sortedSeats,
    variant,
    viewerSeat
  ]);

  return (
    <div className={styles.previewTableFill}>
      <div className={styles.previewTableFill}>
        <Provider store={snapshotStore}>
          <TableIdContext.Provider value=''>
            <OrientationOverrideProvider orientation={viewportOrientation}>
              <ShowAllDealersContext.Provider value={false}>
                <HandHistorySnapshotModeContext.Provider value>
                  <GameProvider>
                    <Table />
                  </GameProvider>
                </HandHistorySnapshotModeContext.Provider>
              </ShowAllDealersContext.Provider>
            </OrientationOverrideProvider>
          </TableIdContext.Provider>
        </Provider>
      </div>
    </div>
  );
};
